import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { DataTable, useDataTable, type DataTableFilter, type DataTableRefs } from '@odo/shared-ui';
import { useProductImageUrls, useProducts, useProductTaxonomy } from '../../hooks/useBilling';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import { useViewportPageSize } from '../../hooks/useViewportPageSize';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { usePageTitle } from '../../hooks/usePageTitle';
import { ProductModal } from '../../components/billing/ProductModal';
import {
    buildProductColumns,
    isProductExpired,
    isWebstoreReady,
    minPriceCents,
    type ProductsSortBy
} from '../../components/billing/products/productColumns';
import { expandProductRows, isSubRow } from '../../components/billing/products/productRows';
import type { ProductRow } from '../../services/billing';

// Products catalog (design: billing Bx catalog): one row per base product,
// versions spliced in as sub-rows on click. All paging is client-side.

// Nulls last, name as tiebreaker, matching the backend's ordering.
function compareProducts(a: ProductRow, b: ProductRow, sortBy: ProductsSortBy): number {
    if (sortBy === 'category') {
        if (a.category === null && b.category !== null) return 1;
        if (a.category !== null && b.category === null) return -1;
        return (a.category ?? '').localeCompare(b.category ?? '') || a.name.localeCompare(b.name);
    }
    if (sortBy === 'price') {
        return minPriceCents(a) - minPriceCents(b) || a.name.localeCompare(b.name);
    }
    return a.name.localeCompare(b.name);
}

// The taxonomy omits deprecated-but-still-held values, the catalog omits live
// values nothing uses yet; either alone leaves a filter unofferable.
function filterOptions(
    taxonomy: readonly string[],
    products: ProductRow[],
    pick: (p: ProductRow) => string | null
): string[] {
    const present = products.map(pick).filter((v): v is string => v !== null);
    return Array.from(new Set([...taxonomy, ...present])).sort();
}

function matchesSearch(product: ProductRow, query: string): boolean {
    const fields = [
        product.name,
        product.category,
        product.subCategory,
        product.vendorName,
        product.marketingCopy,
        ...product.versions.flatMap((version) => [
            version.label,
            version.billId,
            version.vendorPartNumber
        ])
    ];
    return fields.some((field) => field?.toLowerCase().includes(query));
}

type WebstoreState = 'sellable' | 'ready' | 'incomplete';

const WEBSTORE_OPTIONS: { value: WebstoreState; label: string }[] = [
    { value: 'sellable', label: 'Sellable' },
    { value: 'ready', label: 'Ready, not enabled' },
    { value: 'incomplete', label: 'Not ready' }
];

// The dropdown hands back a bare string; anything off the option list clears.
function toWebstoreState(value: string | null): WebstoreState | null {
    return WEBSTORE_OPTIONS.some((option) => option.value === value)
        ? (value as WebstoreState)
        : null;
}

function webstoreState(product: ProductRow): WebstoreState {
    if (product.sellableInWebstore) return 'sellable';
    return isWebstoreReady(product) ? 'ready' : 'incomplete';
}

type ProductFilters = { category: string[]; vendor: string[]; webstore: WebstoreState | null };

// Matches useViewportPageSize's own default.
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;

export function ProductsTable() {
    usePageTitle('Products');
    const { setTitle, setSubtitle } = usePageHeader();
    const { hasPermission } = useMyPermissions();
    const canEditProducts = hasPermission('products.update');

    const table = useDataTable<ProductsSortBy, ProductFilters>({
        defaultSort: { by: 'name', order: 'asc' },
        defaultLimit: VIEWPORT_DEFAULT_PAGE_SIZE,
        defaultFilters: { category: [], vendor: [], webstore: null },
        mode: 'paged'
    });
    const {
        category: categoryFilter,
        vendor: vendorFilter,
        webstore: webstoreFilter
    } = table.filters;

    // Client-side filtering has no debounce to commit through, so plain local
    // state rather than `table.search` - hence the explicit page reset below.
    const [search, setSearch] = useState('');
    const handleSearchChange = (value: string) => {
        setSearch(value);
        table.setPage(1);
    };

    const [expandedId, setExpandedId] = useState<string | null>(null);
    // The id (not the row object) so the open modal re-reads fresh data - e.g.
    // its discount rules list - when the products query refetches after a save.
    const [editingProductId, setEditingProductId] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const { products, isLoading } = useProducts();
    const editingProduct = editingProductId
        ? (products.find((product) => product.id === editingProductId) ?? null)
        : null;

    useEffect(() => {
        setTitle('Products');
        setSubtitle(`${products.length} in catalog`);
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [products.length, setTitle, setSubtitle]);

    const imageFileIds = useMemo(
        () => products.map((p) => p.imageFileId).filter((id): id is string => id !== null),
        [products]
    );
    const { imageUrls } = useProductImageUrls(imageFileIds);

    const { categories: categoryTaxonomy } = useProductTaxonomy();
    const categoryOptions = useMemo(
        () => filterOptions(categoryTaxonomy, products, (p) => p.category),
        [categoryTaxonomy, products]
    );
    // No code-defined taxonomy for the catalogue vendor - it's whatever the
    // Vendors page has created, so the filter only offers vendors in use.
    const vendorOptions = useMemo(
        () =>
            Array.from(
                new Set(products.map((p) => p.vendorName).filter((v): v is string => v !== null))
            ).sort(),
        [products]
    );

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase();
        return products.filter((product) => {
            if (
                categoryFilter.length > 0 &&
                !(product.category && categoryFilter.includes(product.category))
            )
                return false;
            if (
                vendorFilter.length > 0 &&
                !(product.vendorName && vendorFilter.includes(product.vendorName))
            )
                return false;
            if (webstoreFilter && webstoreState(product) !== webstoreFilter) return false;
            if (!query) return true;
            return matchesSearch(product, query);
        });
    }, [products, categoryFilter, vendorFilter, webstoreFilter, search]);

    const sorted = useMemo(() => {
        const direction = table.order === 'asc' ? 1 : -1;
        const now = Date.now();
        return [...filtered].sort((a, b) => {
            // Retired products always sink to the bottom, whatever the sort column.
            const expiredDelta =
                Number(isProductExpired(a, now)) - Number(isProductExpired(b, now));
            if (expiredDelta !== 0) return expiredDelta;
            return direction * compareProducts(a, b, table.sort);
        });
    }, [filtered, table.sort, table.order]);

    const { pageSize, tableCardRef, tableHeaderRef, tableBodyRef, paginationRef, remeasureOnce } =
        useViewportPageSize(VIEWPORT_DEFAULT_PAGE_SIZE);

    const { setLimit } = table;
    useEffect(() => {
        setLimit(pageSize);
    }, [pageSize, setLimit]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / table.limit));
    const currentPage = Math.min(table.page, totalPages);
    const paged = sorted.slice((currentPage - 1) * table.limit, currentPage * table.limit);

    // Sub-rows are spliced in AFTER paging, so a page always holds the same
    // number of products whether or not one of them is open.
    const tableRows = useMemo(() => expandProductRows(paged, expandedId), [paged, expandedId]);

    useLayoutEffect(() => {
        if (paged.length > 0) remeasureOnce();
    }, [paged.length, remeasureOnce]);

    const columns = useMemo(
        () =>
            buildProductColumns({
                expandedId,
                now: Date.now(),
                canEdit: canEditProducts,
                onEdit: setEditingProductId,
                imageUrls
            }),
        [expandedId, canEditProducts, imageUrls]
    );

    // `searchable: true` on both: the catalog's category/vendor lists sit under
    // the shared dropdown's auto-show threshold, the old one always showed it.
    const filters: DataTableFilter[] = [
        {
            key: 'category',
            label: 'Category',
            multiple: true,
            options: categoryOptions,
            value: categoryFilter,
            onChange: (value) => table.setFilter('category', value),
            workQueueVariant: true,
            align: 'right',
            searchable: true
        },
        {
            key: 'vendor',
            label: 'Vendor',
            multiple: true,
            options: vendorOptions,
            value: vendorFilter,
            onChange: (value) => table.setFilter('vendor', value),
            workQueueVariant: true,
            align: 'right',
            searchable: true
        },
        {
            key: 'webstore',
            label: 'Webstore',
            options: WEBSTORE_OPTIONS,
            value: webstoreFilter,
            onChange: (value) => table.setFilter('webstore', toWebstoreState(value)),
            workQueueVariant: true,
            align: 'right'
        }
    ];

    const refs: DataTableRefs = {
        card: tableCardRef,
        head: tableHeaderRef,
        body: tableBodyRef,
        pagination: paginationRef
    };

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <DataTable
                label="Products"
                columns={columns}
                rows={tableRows}
                getRowKey={(row) => row.id}
                rowAction={{
                    // Only a product row toggles: `row.product.id` on a sub-row
                    // is the expanded id, so any click there collapsed the group.
                    onActivate: (row) => {
                        if (isSubRow(row)) return;
                        setExpandedId((current) => (current === row.id ? null : row.id));
                    },
                    mode: 'click'
                }}
                rowClassName={(row) =>
                    // Classes stack: an expired row stays dimmed while expanded.
                    [
                        // DataTable paints every row as clickable while a
                        // rowAction exists; an inert sub-row opts back out.
                        isSubRow(row) ? 'bg-gray-50/60 !cursor-default hover:!bg-gray-50/60' : null,
                        row.kind === 'version' ? '!h-12' : null,
                        row.kind === 'versionsHeader' ? '!h-8' : null,
                        row.kind === 'product' && expandedId === row.id ? 'bg-gray-50/70' : null,
                        isProductExpired(row.product, Date.now()) ? 'opacity-60' : null
                    ]
                        .filter(Boolean)
                        .join(' ') || undefined
                }
                sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                mode="paged"
                pagination={{
                    page: currentPage,
                    totalPages,
                    total: sorted.length,
                    onPage: (p) => table.setPage(Math.min(Math.max(1, p), totalPages))
                }}
                isLoading={isLoading}
                loadingRows={
                    <tr>
                        <td colSpan={8} className="px-3 py-8 text-center">
                            <div className="flex items-center justify-center gap-2 text-gray-400">
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                                <span className="text-sm">Loading products…</span>
                            </div>
                        </td>
                    </tr>
                }
                emptyState="No products found."
                refs={refs}
                className="overflow-hidden px-8 py-4"
                search={{
                    value: search,
                    onChange: handleSearchChange,
                    placeholder: 'Search product, version, part number or Bill.com ID…'
                }}
                filters={filters}
                actions={
                    canEditProducts
                        ? [
                              {
                                  key: 'new-product',
                                  label: '+ New product',
                                  variant: 'primary',
                                  onClick: () => setShowCreateModal(true)
                              }
                          ]
                        : undefined
                }
            />

            {showCreateModal && (
                <ProductModal product={null} onClose={() => setShowCreateModal(false)} />
            )}

            {editingProduct && (
                <ProductModal product={editingProduct} onClose={() => setEditingProductId(null)} />
            )}
        </div>
    );
}
